import * as survey from "@aidkitorg/types/lib/survey";
import { CompileExpressionToSQL, nameForColumn } from "@aidkitorg/types/lib/translation/expr_to_sql";
import { expandGenericTemplates } from "@aidkitorg/types/lib/translation/templates"
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, KeyboardShortcutsHelp, Spinner, useIsMobile, useLocalizedStrings, useToast } from "@aidkitorg/component-library";
import { checkPageAccess } from "@aidkitorg/types/lib/admin-panel/permissions";
import { usePost } from "./API";
import InterfaceContext, { UserInfoContext } from "./Context";
import { DashboardComponent } from "./DistroDashboard";
import { copyToClipboard, getDistroBrowserIncompatibility } from "./Util";
import { queryTreePath, SavedExploreQuery, savedFolderPaths, SavedQueriesSidebar, SaveQueryModal, savedQueryLink, toVersion } from "./ExploreSavedQueries";
import { displayFolderPath, QueryScope, splitTreePath } from "./exploreFolderTree";

function utf8_to_b64(str: string) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

function b64_to_utf8(str: string) {
  return decodeURIComponent(escape(window.atob(str)));
}

function tryDecodingHash(hash: string) {
  try {
    return JSON.parse(b64_to_utf8(hash));
  } catch (e) {
    return null;
  }
}

export function encodeQueueForExplore(q: survey.Queue, sectionFilter?: survey.BooleanExpr): string {
  // create 'And' expression to include sectionFilters if needed
  let andFilter = {
    kind: 'And',
    clauses: [
      q.condition.kind === 'Click' ? q.condition.expr : q.condition,
      ...(sectionFilter ? [sectionFilter] : []), // only add sectionFilter if it exists
      {
        'kind': 'Value Empty',
        'value': {
          'kind': 'Field',
          'field': q.assigneeField
        }
      }
    ]};

  // put the andFilter into an applicant table view of the queue - including queue order (if exists)
  const exploreQuery = {
    query: {
      kind: 'Applicant Table',
      filter: q.condition.kind === 'Click' ? {
        kind: 'Click',
        expr: andFilter,
        orderBy: q.condition.orderBy
      } : andFilter,
      columns: [
        { kind: 'Field', field: 'legal_name'},
        ...(q.columns || []),
      ]
    }
  };
  return utf8_to_b64(JSON.stringify(exploreQuery));
}

function exploreLlmContext(fieldScope: string[]) {
  return `We are using a tool to generate queries and charts to explore data in a database. These queries and charts are defined using JSON.

            The database has the following fields: ${fieldScope.join(', ')}

            As an example, this is a bar chart that shows the race of veterans: {"kind":"Bar Chart","title":"Gender of people who like the color blue","summary":{"kind":"Count"},"groups":[{"kind":"Field","field":"race"}],"filter":{"kind":"And","clauses":[{"kind":"Equals","field":"veteran","value":"yes"}]}}

            This is a table of all applicants with their name and birth date: {"kind":"Applicant Table","columns":[{"kind":"Field","field":"legal_name"},{"kind":"Field","field":"birth_date"}],"filter":{"kind":"Not","clause":{"kind":"Never"}}}

            Note: that to check if a field equals a value, the correct form is {"kind": "Equals", field: [FIELD], value: [VALUE]}`;
}

/** The query /explore starts with: an applicant table that matches no one. A fresh object each time. */
function blankExploreQuery(): survey.Explore {
  return { query: { kind: 'Applicant Table', columns: [], filter: { kind: 'Never' } } };
}

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));
type OpenSavedKey = { id: string, scope: QueryScope, version: string | null };

export function Explore() {
  const getScope = usePost('/program/get_fields_scope');
  const doMagic = usePost('/program/admin/magic');
  const loadDashboardPost = usePost('/dashboard/load');
  const listSavedQueries = usePost('/program/admin/explore_query/list', { suppressErrorDisplay: true });
  const getSavedQuery = usePost('/program/admin/explore_query/get', { suppressErrorDisplay: true });
  const listGlobalQueries = usePost('/program/admin/explore_query/global/list', { suppressErrorDisplay: true });
  const getGlobalQuery = usePost('/program/admin/explore_query/global/get', { suppressErrorDisplay: true });
  const context = useContext(InterfaceContext);
  const params = new URLSearchParams(window.location.search);
  const user = useContext(UserInfoContext);
  // Shapes what is shown based on access.
  const canViewGlobal = checkPageAccess(user.tags, 'explore-global', 'view');
  const canEditGlobal = checkPageAccess(user.tags, 'explore-global', 'edit');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [showQuery, setShowQuery] = useState(false);
  const [dash, setDash] = useState<any>(null);
  const defaultQuery: survey.Explore = tryDecodingHash(window.location.hash.slice(1)) || blankExploreQuery();
  const [query, setQuery] = useState(defaultQuery);
  // queryRef is kept in sync with query state and is used by the keyboard shortcut handler
  // to avoid stale closures. Always update both together.
  const queryRef = useRef(defaultQuery);
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { toast } = useToast();
  const runTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();
  const L = useLocalizedStrings();
  const fieldScopeRef = useRef<string[] | null>(null);
  const [savedQueries, setSavedQueries] = useState<SavedExploreQuery[] | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const savedIdToOpen = window.location.hash.slice(1) ? null : params.get('saved');
  const savedScopeToOpen: QueryScope = params.get('scope') === 'global' ? 'global' : 'program';
  const [loadingSavedQuery, setLoadingSavedQuery] = useState(!!savedIdToOpen);
  const [openSavedKey, setOpenSavedKey] = useState<OpenSavedKey | null>(
    params.get('saved') ? { id: params.get('saved')!, scope: savedScopeToOpen, version: params.get('version') } : null);
  const openSaved = savedQueries?.find(q => q.id === openSavedKey?.id && q.scope === openSavedKey?.scope) ?? null;
  // Set once another query is opened (or the editor cleared) while a ?saved= link is still loading, so the
  // link's late response doesn't replace what the user switched to.
  const linkLoadSupersededRef = useRef(false);
  const savedFolders = useMemo(() => savedFolderPaths(savedQueries ?? []), [savedQueries]);

  function setOpenSaved(key: OpenSavedKey | null) {
    setOpenSavedKey(key);
    const url = new URL(window.location.href);
    url.searchParams.delete('saved');
    url.searchParams.delete('scope');
    url.searchParams.delete('version');
    if (key) {
      url.searchParams.set('saved', key.id);
      if (key.scope === 'global') url.searchParams.set('scope', 'global');
      if (key.version) url.searchParams.set('version', key.version);
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }

  useEffect(() => {
    if (params.get('run') === 'yes' && !savedIdToOpen) {
      // This runs before the editor's first onChange, so expand templates here, as onChange would.
      queryRef.current = expandGenericTemplates(queryRef.current);
      run();
    }
  }, [])

  useEffect(() => {
    if (!savedIdToOpen) return;
    (async () => {
      let decoded: survey.Explore | null = null;
      let version: string | null = null;
      let denied = false;
      try {
        const result = await (savedScopeToOpen === 'global' ? getGlobalQuery : getSavedQuery)({ id: savedIdToOpen });
        decoded = result && 'query' in result && result.query ? tryDecodingHash(result.query.query) : null;
        version = result && 'query' in result && result.query ? toVersion(result.query.updated_at) : null;
        denied = !!result && 'error' in result && result.error === 'permission_denied';
      } catch (e) {
        console.error('Failed to load saved query', e);
      }
      if (linkLoadSupersededRef.current) {
        setLoadingSavedQuery(false);
        return;
      }
      if (denied) {
        toast({
          description: savedScopeToOpen === 'global'
            ? "You don't have permission to view Global saved queries."
            : "You don't have permission to view that saved query.",
          variant: 'error',
        });
        setOpenSaved(null);
      } else if (decoded) {
        setOpenSaved({ id: savedIdToOpen, scope: savedScopeToOpen, version });
        window.location.hash = '#' + utf8_to_b64(JSON.stringify(decoded));
        // The editor gets the query as saved (templates intact); runs use the expanded form, as onChange does.
        queryRef.current = expandGenericTemplates(decoded);
        setQuery(decoded);
      } else {
        toast({ description: 'That saved query could not be loaded.', variant: 'error' });
        setOpenSaved(null);
      }
      // Set the query before this, so the editor mounts with it. On failure it mounts with the default query.
      setLoadingSavedQuery(false);
      if (decoded && params.get('run') === 'yes') run();
    })();
  }, []);

  const refreshSavedQueries = useCallback(async (justSaved?: { id: string, scope: QueryScope } | null) => {
    // Always ask for Global: tags may not have loaded yet, and the server refuses users without explore-global.view.
    const [programResult, globalResult] = await Promise.all([listSavedQueries({}), listGlobalQueries({})]);
    if (!(programResult && 'queries' in programResult && programResult.queries)) return;
    let globalQueries = globalResult && 'queries' in globalResult && globalResult.queries ? globalResult.queries : [];
    if (justSaved?.scope === 'global') {
      const fresh = await getGlobalQuery({ id: justSaved.id });
      if (fresh && 'query' in fresh && fresh.query) {
        globalQueries = globalQueries.filter(q => q.id !== justSaved.id).concat(fresh.query);
      }
    }
    setSavedQueries([
      ...programResult.queries.map(q => ({ ...q, scope: 'program' as const })),
      ...globalQueries.map(q => ({ ...q, scope: 'global' as const })),
    ]);
  }, [listSavedQueries, listGlobalQueries, getGlobalQuery]);

  useEffect(() => {
    refreshSavedQueries(openSavedKey);
  }, []);

  /** Replaces the editor contents used  when opening a saved query. */
  function loadIntoEditor(value: survey.Explore) {
    // Cancel a still running query as it's results would be displayed under the newly opened one.
    cancel();
    linkLoadSupersededRef.current = true;
    window.location.hash = '#' + utf8_to_b64(JSON.stringify(value));
    const expanded = expandGenericTemplates(value);
    queryRef.current = expanded;
    setQuery(expanded);
    setResults(null);
    const fieldScope = fieldScopeRef.current;
    distroRef.current?.initialize(value, user, undefined, undefined,
      fieldScope ? { 'fields': fieldScope } : undefined,
      fieldScope ? exploreLlmContext(fieldScope) : undefined);
  }

  /** The query to save, read only after any edit still in Distro's debounce has reached the hash. */
  async function readQueryAfterPendingEdits() {
    await new Promise(resolve => setTimeout(resolve, 600));
    // The hash is empty until the query is first edited, so fall back to encoding the default.
    return window.location.hash.slice(1) || utf8_to_b64(JSON.stringify(queryRef.current));
  }

  /**
   * Starts over with a blank query, detached from any open saved query (so Save offers a new query, not an update).
   */
  async function startNewQuery() {
    const previousSaved = openSavedKey;
    // Wait for edits, so Undo restores the query as last typed.
    const previous = await readQueryAfterPendingEdits();
    const previousQuery = window.location.hash.slice(1) ? tryDecodingHash(previous) : null;
    loadIntoEditor(blankExploreQuery());
    setOpenSaved(null);
    const shown = toast({
      description: 'Started a new query.',
      variant: 'success',
      action: previousQuery ? (
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={() => {
            loadIntoEditor(previousQuery);
            setOpenSaved(previousSaved);
            shown.dismiss();
          }}
        >
          Undo
        </Button>
      ) : undefined,
    });
  }

  function openSavedQuery(saved: SavedExploreQuery) {
    const decoded = tryDecodingHash(saved.query);
    if (!decoded) {
      toast({ description: `"${saved.name}" could not be opened.`, variant: 'error' });
      return;
    }
    loadIntoEditor(decoded);
    setOpenSaved({ id: saved.id, scope: saved.scope, version: toVersion(saved.updated_at) });
    toast({ description: `Opened "${saved.name}"`, variant: 'success' });
  }

  function copySavedQueryLink(saved: SavedExploreQuery) {
    copyToClipboard(savedQueryLink(saved.id, saved.scope));
    toast({ description: `Copied a link to "${saved.name}".`, variant: 'success' });
  }

  const run = useCallback(async () => {
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const component = queryRef.current.query;
    const dashboard: survey.Dashboard = {
      kind: 'Dashboard',
      version: 1,
      path: '',
      title: 'Explore',
      components: [component]
    };
    setDash(dashboard);
    setRunning(true);
    try {
      type LoadDashboardResult = Awaited<ReturnType<typeof loadDashboardPost>>;
      const loadResult = await Promise.race<LoadDashboardResult>([
        loadDashboardPost(
          { dashboardRef: 'explore', dashboardComponent: component },
          abortControllerRef.current.signal
        ),
        new Promise<never>((_, reject) => setTimeout(
          () => reject(new Error('Query timeout')),
          30000
        ))
      ]);

      // loadResult is TimedQueryResult | { error: string }; rowCount/rows
      // only exist on the success branch, hence the Partial cast for access.
      const result = loadResult as Partial<Extract<LoadDashboardResult, { rows: unknown }>> & { error?: string };
      const enriched: any = { ...component };
      enriched.error = result.error;

      switch (component.kind) {
        case 'Hero Count':
          enriched.results = result.rowCount ?? 0;
          break;
        case 'Bar Chart': {
          const bc = component as survey.BarChart;
          enriched.results = result.rows ?? [];
          enriched.groupKeys = bc.groups.map(g => nameForColumn(g) ?? '');
          enriched.stackKeys = bc.bars?.values.map(s => nameForColumn(s)).filter((k): k is string => k !== null);
          enriched.stack = bc.bars?.kind === 'Stacked';
          enriched.summaryKey = nameForColumn(bc.summary) ?? '';
          break;
        }
        case 'Pie Chart': {
          const pc = component as survey.PieChart;
          enriched.results = result.rows ?? [];
          enriched.groupKeys = pc.groups.map(g => nameForColumn(g) ?? '');
          enriched.summaryKey = nameForColumn(pc.summary) ?? '';
          break;
        }
        case 'Custom Query': {
          const cq = component as survey.CustomQuery;
          if ('download' in cq.visualization && cq.visualization.download) {
            cq.visualization.download.downloadPath = '0';
          }

          switch (cq.visualization.kind) {
            case 'Custom Number':
              if (result.rowCount === 1 && result.rows?.[0]) {
                const keys = Object.keys(result.rows[0]);
                enriched.results = keys.length === 1 ? result.rows[0][keys[0]] : result.rows[0];
              } else {
                enriched.error = enriched.error || 'Invalid count query. Did not return exactly one row';
              }
              break;
            default:
              enriched.results = result.rows ?? [];
              break;
          }
          break;
        }
        case 'Applicant Table': {
          const aq = component as survey.ApplicantTable;
          if (aq.download) {
            aq.download.downloadPath = '0';
          }
          enriched.results = result.rows ?? [];
          break;
        }

        default:
          enriched.results = result.rows ?? [];
          break;
      }

      setResults({ components: [enriched] });
    } catch (e:any) {
      if (e.name !== 'AbortError') {
        toast({
          description: e.message,
          variant: 'error'
        })
      }
    } finally {
      setRunning(false);
    }
  }, []);

  const handleRunQueryKeyboardShortcut = useCallback(async () => {
    // Cancel any pending runs before starting a new one.
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }
    // If the query is already running, instead cancel it.
    if (running) {
      await cancel();
      return;
    };
    setRunning(true);
    // Distro has a debounce timeout of 500ms, so we need to make sure that has triggered before
    // we run the query.
    runTimeoutRef.current = setTimeout(() => run(), 600);
  }, [cancel, run, running])

  // Keyboard shortcuts dialog
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const toggleKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp(prev => !prev);
  }, []);
  const closeKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp(false);
  }, []);

  async function cancel() {
    // Also drop a run the keyboard shortcut queued but hasn't started, or it would still fire afterward.
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setRunning(false);
  }

  (window as any).magic = async (data: any) => {
    return await doMagic(data);
  }

  useEffect(() => {
    if (loadingSavedQuery) return;
    (async () => {
      const scope = await getScope({});
      if (scope?.fieldScope) {
        fieldScopeRef.current = scope.fieldScope;
        // Re-read the hash after a saved query has been opened.
        const current = tryDecodingHash(window.location.hash.slice(1)) ?? query;
        distroRef.current?.initialize(current, user, undefined, undefined, {
          'fields': scope.fieldScope,
        }, exploreLlmContext(scope.fieldScope));
      }
    })()
  }, [user, loadingSavedQuery]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return <div className="bg-gray-100 h-full flex flex-col md:flex-row">
    {savedQueries !== null && (
      <SavedQueriesSidebar
        queries={savedQueries}
        currentUserId={user.uid}
        onOpen={openSavedQuery}
        onCopyLink={copySavedQueryLink}
        onDeleted={(deleted) => {
          toast({ description: `Deleted "${deleted.name}".`, variant: 'success' });
          if (deleted.id === openSavedKey?.id && deleted.scope === openSavedKey?.scope) setOpenSaved(null);
          refreshSavedQueries();
        }}
        onUpdated={(updated, versions) => {
          toast({ description: `Updated "${updated.name}".`, variant: 'success' });
          // The editor still holds the version that was just edited, so it moves to the new version. 
          // If it holds an older one, it keeps it, so saving it is still refused.
          if (updated.id === openSavedKey?.id && updated.scope === openSavedKey.scope && openSavedKey.version === versions.from) {
            setOpenSaved({ ...openSavedKey, version: versions.to });
          }
          refreshSavedQueries({ id: updated.id, scope: updated.scope });
        }}
        onStale={() => refreshSavedQueries()}
        onFolderRenamed={(from, to, versions) => {
          toast({ description: `Moved folder "${displayFolderPath(from)}" to "${displayFolderPath(to)}".`, variant: 'success' });
          // The move bumped the open query's version. Like an edit, the editor follows only if it held the version
          // that was moved, so an editor that was already out of date is still refused.
          const moved = versions.find(v => v.id === openSavedKey?.id);
          if (moved && openSavedKey && openSavedKey.scope === splitTreePath(from).scope && openSavedKey.version === moved.from) {
            setOpenSaved({ ...openSavedKey, version: moved.to });
          }
          refreshSavedQueries();
        }}
        folders={savedFolders}
        openQueryKey={openSavedKey}
        canViewGlobal={canViewGlobal}
        canEditGlobal={canEditGlobal}
      />
    )}
    <div className="flex-1 min-w-0 p-4">
      <SaveQueryModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        getQuery={readQueryAfterPendingEdits}
        // Without explore-global.edit, an open Global query can only be saved as a program query.
        openQuery={openSaved?.scope === 'global' && !canEditGlobal ? null : openSaved}
        openVersion={openSavedKey?.version ?? null}
        onStale={() => refreshSavedQueries()}
        folders={savedFolders}
        canSaveToGlobal={canEditGlobal}
        onSaved={(saved) => {
          toast({ description: `${saved.updated ? 'Updated' : 'Saved'} "${saved.name}".`, variant: 'success' });
          setOpenSaved({ id: saved.id, scope: saved.scope, version: saved.version });
          refreshSavedQueries({ id: saved.id, scope: saved.scope });
        }}
      />
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2>Query</h2>
        {openSaved && (
          <span className="text-sm text-gray-600">
            Saved query: <strong className="break-words">
              {queryTreePath(openSaved) ? `${displayFolderPath(queryTreePath(openSaved))}/${openSaved.name}` : openSaved.name}
            </strong>
          </span>
        )}
      </div>
      {openSaved?.description && (
        <p className="mt-1 max-w-3xl whitespace-pre-wrap break-words text-sm text-gray-600">{openSaved.description}</p>
      )}
      {openSaved?.scope === 'global' && (
        <p className="mt-2 flex max-w-3xl items-start gap-1.5 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800" role="note">
          <Icon name="ExclamationTriangleIcon" size="sm" />
          <span>
            This is a Global query. Target fields differ between programs, so it may not run here.
          </span>
        </p>
      )}
      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        {loadingSavedQuery ? <Spinner size="small" /> : <Suspense>
          <Distro 
            ref={distroRef} 
            value= {query}
            types='src/survey.ts'
            name='Explore'
            onChange={(v) => {
              window.location.hash = '#' + utf8_to_b64(JSON.stringify(v));
              const expanded = expandGenericTemplates(v);
              queryRef.current = expanded;
              setQuery(expanded);
            }}
          />
        </Suspense>}
      </div>
      <div className="flex justify-between">
        <div className="mt-2 inline-flex">
          <Button
            onClick={run}
            type="button"
            loading={running}
            isDisabled={running}
            variant="primary"
            className='m-2'
          >
            Run
          </Button>
          {running && <Button
            onClick={cancel}
            type="button"
            variant="danger"
            className='m-2'
          >
            Cancel
          </Button>}
          {/* Hidden while a ?saved= link loads: the editor doesn't hold that query yet. */}
          {savedQueries !== null && !loadingSavedQuery && (
            <Button
              onClick={() => setShowSaveModal(true)}
              type="button"
              variant="secondary"
              className='m-2'
            >
              Save
            </Button>
          )}
          <Button
            onClick={startNewQuery}
            type="button"
            variant="secondary"
            className='m-2'
          >
            Clear
          </Button>
          {['Applicant Table', 'Bar Chart', 'Pie Chart', 'Hero Count'].includes(query.query.kind) && (
            <Button
              onClick={() => setShowQuery(!showQuery)}
              type="button"
              variant="secondary"
              className='m-2'
            >
              {showQuery ? 'Hide' : 'Show'} SQL
            </Button>
          )}
        </div>
        <div className='mt-2'>
          {!isMobile && (<Button
            variant="unstyled"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toggleKeyboardHelp();
            }}
            icon={<Icon name="QuestionMarkCircleIcon" type="solid" />}
            aria-label={L.component_library.keyboard_shortcuts_help.show_keyboard_shortcuts}
            className={`p-2 bg-gray-300 hover:bg-gray-300 rounded-full`}
          />)}
          <KeyboardShortcutsHelp
            title="Keyboard Shortcuts"
            shortcuts={[
              { key: 'Enter', description: 'Run/cancel the query', modifier: 'meta', callback: handleRunQueryKeyboardShortcut },
              { key: '/', description: 'Comment/uncomment SQL', modifier: 'meta', /* handled by CodeEditor */ },
            ]}
            isOpen={showKeyboardHelp}
            onClose={closeKeyboardHelp}
          />
        </div>
      </div>
      <div className="mt-4">
        {showQuery && <pre>{(() => {
          const q = query.query;
          switch (q.kind) {
            case 'Applicant Table':
              return CompileExpressionToSQL({
                cond: q.filter.kind === 'Click' ? q.filter.expr : q.filter,
                columns: q.columns,
                orderBy: q.filter.kind === 'Click' ? q.filter.orderBy : undefined,
                usePivot: q.usePivot,
              });
            case 'Bar Chart':
              return CompileExpressionToSQL({
                cond: q.filter ?? { kind: 'Not', clause: { kind: 'Never' } },
                columns: [q.summary],
                groups: [...q.groups, ...(q.bars?.values || [])],
                countEmpty: q.countEmpty,
                allowCommasInGroups: q.allowCommasInGroups
              });
            case 'Pie Chart':
              return CompileExpressionToSQL({
                cond: q.filter ?? { kind: 'Not', clause: { kind: 'Never' } },
                columns: [q.summary],
                groups: q.groups,
                countEmpty: q.countEmpty,
                allowCommasInGroups: q.allowCommasInGroups
              });
            case 'Hero Count':
              return CompileExpressionToSQL({
                cond: q.filter
              });
            default:
              return '';
          }
        })()}
        </pre>}
        {!!results &&
          (typeof results === 'string'
            ? <div>{results}</div>
            : (<>
              <h1>Results</h1>
              {(results.components || []).map((s: survey.DashboardComponent & { sql?: string; error?: string }, i: number) => {
                if (s.error && s.sql) {
                  return <div key={i} className="mb-4">
                    <p className="text-red-600 font-medium">Error: {s.error}</p>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-gray-600">SQL that was run</summary>
                      <pre className="mt-2 p-2 bg-gray-100 text-xs overflow-auto max-h-60">{s.sql}</pre>
                    </details>
                  </div>;
                }
                return <DashboardComponent
                  key={s.kind + i}
                  section={s}
                  updateFilterConfig={(path: string, idx: number) => {}}
                  isExploreQuery={true}
                  dashboard={ dash }
                  filterConfig={{} as Record<string, number>}
                  lang={context.lang}
                />
              })}
            </>))}
      </div>
    </div>
  </div>
}
